// ─── Public Status Page — the page ───────────────────────────────────
//
// This file owns EVERYTHING structural: the route, the data loop, the section
// chrome (<section> + <h2>) and all derived state (the aggregated overview
// history, the incident filter counts, the cold deep-link fetch). Every
// component under ./components is pure presentation and takes the props
// declared in ./types.ts.
//
// It is deliberately one file. The public page has exactly two routes and one
// payload; splitting its composition across a router config, a layout and three
// containers would add indirection to something a reader must be able to hold in
// their head while an outage is in progress.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  STATUS_FILTER_LABELS,
  formatIncidentRefId,
  parseIncidentRef,
  type PublicStatusComponent,
  type PublicStatusIncident,
  type StatusFilterLabel,
} from '@pcp/shared';

import { ComponentDetail } from './components/ComponentDetail';
import { DayDetail } from './components/DayDetail';
import { ComponentTree } from './components/ComponentTree';
import { DegradedNotice } from './components/DegradedNotice';
import { HistoryGrid } from './components/HistoryGrid';
import { IncidentDetail } from './components/IncidentDetail';
import { IncidentFilters } from './components/IncidentFilters';
import { IncidentList } from './components/IncidentList';
import { StatusBanner } from './components/StatusBanner';
import { StatusFooter } from './components/StatusFooter';
import { StatusHeader } from './components/StatusHeader';
import { StatusShell } from './components/StatusShell';
import { SubscriptionPage } from './components/SubscriptionPage';
import { SubscribeSheet } from './components/SubscribeSheet';
import { da } from './i18n/da';
import { aggregateHistory } from './lib/levels';
import { fetchIncident } from './lib/statusApi';
import { useStatusData } from './lib/useStatusData';
import { buildStatusPath, useStatusRouter } from './router';
import { STATUS_MAIN_ID, type IncidentFilterOption } from './types';
import { SIcon } from './ui/SIcon';
import { stripHtml } from './ui/RichText';

/** Stable empty array — a fresh `[]` per render would re-run every useMemo. */
const NO_INCIDENTS: PublicStatusIncident[] = [];
const NO_COMPONENTS: PublicStatusComponent[] = [];
/** Stands in for `summary.dayMinutes` before the payload lands, and for a v2
 *  snapshot that has none — an empty denominator makes the figure fall back to
 *  counting days rather than dividing by nothing. */
const NO_MINUTES: number[] = [];

/**
 * Does this incident answer to the ref in the URL? Uses the shared parser so all
 * three legal forms resolve: our uuid, our ref id (i-00042, i-42, I-00042) and a
 * preserved StatusCast id — the last one is what keeps the old page's deep links
 * alive in emails, tickets and forum posts after cutover.
 */
function matchesRef(incident: PublicStatusIncident, ref: string): boolean {
  const parsed = parseIncidentRef(ref);
  if (!parsed) return false;
  if (parsed.kind === 'id') return incident.id.toLowerCase() === parsed.value.toLowerCase();
  if (parsed.kind === 'number') return incident.refId === formatIncidentRefId(parsed.value);
  const value = parsed.value.toLowerCase();
  return (
    (incident.sourceRef ?? '').toLowerCase() === value ||
    incident.refId.toLowerCase() === value ||
    incident.id.toLowerCase() === value
  );
}

/** Everything a visitor could reasonably type into the search box. */
function incidentSearchText(incident: PublicStatusIncident): string {
  const parts: string[] = [incident.refId, incident.title, incident.statusLabel];
  if (incident.body) parts.push(stripHtml(incident.body));
  for (const update of incident.updates ?? []) {
    parts.push(update.statusLabel);
    if (update.body) parts.push(stripHtml(update.body));
  }
  for (const component of incident.components ?? []) parts.push(component.name);
  for (const root of incident.roots ?? []) parts.push(root.name);
  return parts.join(' ').toLowerCase();
}

/** Pinned first, then newest — the order the list is read in. */
function byPinnedThenRecent(a: PublicStatusIncident, b: PublicStatusIncident): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime();
}

/** Empty ancestor trail for a root — a module constant, so a root's detail page
 *  does not get a fresh array (and a fresh render) out of every poll. */
const NO_ANCESTORS: Array<{ id: string; name: string }> = [];

/**
 * Depth-first search for a component in the tree, returning it together with its
 * ancestors ROOT FIRST — the detail page needs both, and finding one without the
 * other would mean walking the tree twice.
 */
function findComponent(
  nodes: PublicStatusComponent[],
  id: string,
  trail: Array<{ id: string; name: string }> = NO_ANCESTORS,
): { component: PublicStatusComponent; ancestors: Array<{ id: string; name: string }> } | null {
  for (const node of nodes) {
    if (node.id === id) return { component: node, ancestors: trail };
    const hit = findComponent(node.children, id, [...trail, { id: node.id, name: node.name }]);
    if (hit) return hit;
  }
  return null;
}

/** Every id in a subtree, so an incident against a CHILD still shows on the
 *  parent's page — the parent's status, grid and uptime already include it, and
 *  an incident list that disagreed with the bar above it would read as a bug. */
function subtreeIds(node: PublicStatusComponent): Set<string> {
  const ids = new Set<string>();
  const walk = (n: PublicStatusComponent): void => {
    ids.add(n.id);
    for (const child of n.children) walk(child);
  };
  walk(node);
  return ids;
}

export function StatusApp() {
  const { route, navigate } = useStatusRouter();
  // The subscriber-facing routes (/manage, /unsubscribe) are deliberately not
  // audience-scoped: they come out of an email, where the subscription is the
  // subject rather than a slice of the page.
  const audience = route.name === 'manage' || route.name === 'unsubscribe' ? null : route.audience;
  const { data, source, fetchedAt, status, failures, refresh } = useStatusData(audience);

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<StatusFilterLabel[]>([]);
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [coldIncident, setColdIncident] = useState<PublicStatusIncident | null>(null);
  const [coldMiss, setColdMiss] = useState(false);

  const incidents = data?.incidents ?? NO_INCIDENTS;
  const historyDays = data?.historyDays ?? 0;
  const historyStartDate = data?.historyStartDate ?? '';
  // The button follows ONE setting: whether public subscription is open at all.
  // It used to also require a live channel, which hid it whenever email and SMS
  // both happened to be off — so the page silently offered no way to sign up,
  // for a reason no visitor could see. A signup on an inactive channel is now
  // accepted and held until an operator enables it (see the note at the top of
  // SubscribeSheet), so there is nothing left for the button to guard.
  const subscribeEnabled = Boolean(data?.subscribeEnabled);

  // ── Navigation ──────────────────────────────────────────────────────
  const goOverview = useCallback(
    (slug: string | null) => navigate({ name: 'overview', audience: slug }),
    [navigate],
  );
  const openIncident = useCallback(
    (incident: PublicStatusIncident) => navigate({ name: 'incident', audience, ref: incident.refId }),
    [navigate, audience],
  );
  const hrefForIncident = useCallback(
    (incident: PublicStatusIncident) => buildStatusPath({ name: 'incident', audience, ref: incident.refId }),
    [audience],
  );
  const openComponent = useCallback(
    (component: { id: string }) => navigate({ name: 'component', audience, id: component.id }),
    [navigate, audience],
  );
  const hrefForComponent = useCallback(
    (component: { id: string }) => buildStatusPath({ name: 'component', audience, id: component.id }),
    [audience],
  );
  // Day navigation. STABLE callbacks, not inline arrows: they are threaded down
  // through ComponentTree → ComponentRow → HistoryGrid → HistoryGridRow, which is
  // memoised precisely so a poll does not re-render 28 rows × 90 cells. A new
  // function identity on every render would defeat that.
  const openDay = useCallback(
    (date: string, componentId: string | null) => navigate({ name: 'day', audience, date, componentId }),
    [navigate, audience],
  );
  const hrefForDay = useCallback(
    (date: string, componentId: string | null) => buildStatusPath({ name: 'day', audience, date, componentId }),
    [audience],
  );
  const overviewHref = useMemo(() => buildStatusPath({ name: 'overview', audience }), [audience]);
  const goHome = useCallback(() => goOverview(audience), [goOverview, audience]);

  // ── Manual refresh ──────────────────────────────────────────────────
  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    refresh();
  }, [refresh]);
  // Any settled load — success or failure — ends the spinner.
  useEffect(() => {
    setRefreshing(false);
  }, [fetchedAt, failures]);

  // A different audience is a different page: its filters mean nothing here.
  useEffect(() => {
    setSelectedLabels([]);
    setQuery('');
  }, [audience]);

  // The sheet must not survive the setting being turned off mid-session.
  useEffect(() => {
    if (!subscribeEnabled) setSubscribeOpen(false);
  }, [subscribeEnabled]);


  // ── Incident deep link ──────────────────────────────────────────────
  const routeRef = route.name === 'incident' ? route.ref : null;
  const inlineIncident = useMemo(
    () => (routeRef ? (incidents.find((incident) => matchesRef(incident, routeRef)) ?? null) : null),
    [incidents, routeRef],
  );
  // Depend on the ID, not the object: the payload is re-fetched on a timer and
  // every poll produces a new-but-equal object, which would otherwise re-trigger
  // the cold fetch below forever.
  const inlineIncidentId = inlineIncident?.id ?? null;

  useEffect(() => {
    if (!routeRef || inlineIncidentId) {
      setColdIncident(null);
      setColdMiss(false);
      return;
    }
    // Wait for the summary before declaring a ref "cold" — it may well be in the
    // payload that is still in flight.
    if (status === 'loading') return;

    let cancelled = false;
    setColdMiss(false);
    void fetchIncident(routeRef, audience).then((found) => {
      if (cancelled) return;
      setColdIncident(found);
      setColdMiss(!found);
    });
    return () => {
      cancelled = true;
    };
  }, [routeRef, inlineIncidentId, audience, status]);

  const detailIncident = inlineIncident ?? coldIncident;
  const detailState: 'loading' | 'ready' | 'notfound' = detailIncident
    ? 'ready'
    : coldMiss
      ? 'notfound'
      : 'loading';

  // ── Derived overview data ───────────────────────────────────────────
  const allComponents = useMemo<PublicStatusComponent[]>(() => {
    if (!data) return NO_COMPONENTS;
    // Flattened depth-first. The page-wide overview bar aggregates the LEAVES
    // and the roots alike — a parent's bar is already the fold of its children,
    // so counting both would double-weight a deep branch. Only nodes with no
    // children contribute; a childless root is a leaf.
    const flatten = (nodes: PublicStatusComponent[]): PublicStatusComponent[] =>
      nodes.flatMap((c) => (c.children.length ? flatten(c.children) : [c]));
    return flatten(data.components);
  }, [data]);

  // The page-wide bar. The server folds it (v3 onward) because the downtime half
  // is an interval union no client can rebuild from folded day characters; the
  // local aggregate stays as the fallback for a v2 snapshot, where the figure
  // degrades to whole days rather than disappearing.
  const overviewHistory = useMemo(
    () =>
      data?.overallHistory ||
      aggregateHistory(allComponents.map((component) => component.history ?? ''), historyDays),
    [data?.overallHistory, allComponents, historyDays],
  );
  const overviewDowntime = data?.overallDowntime;
  const dayMinutes = data?.dayMinutes;

  const activeIncidents = useMemo(
    () => incidents.filter((incident) => incident.state === 'open').sort(byPinnedThenRecent),
    [incidents],
  );

  // ── Component deep link ─────────────────────────────────────────────
  // No cold fetch: unlike an incident archive, every component the page can show
  // is in the summary by construction. So "not found" is decided the moment the
  // payload lands — an id that is not in the tree is either archived, or scoped
  // to an audience this page is not.
  const routeComponentId = route.name === 'component' ? route.id : null;
  const componentHit = useMemo(
    () => (routeComponentId && data ? findComponent(data.components, routeComponentId) : null),
    [data, routeComponentId],
  );
  const componentIncidents = useMemo(() => {
    if (!componentHit) return NO_INCIDENTS;
    const ids = subtreeIds(componentHit.component);
    return incidents
      .filter((incident) => (incident.components ?? []).some((c) => ids.has(c.id)))
      .sort(byPinnedThenRecent);
  }, [componentHit, incidents]);

  // ── Document title ──────────────────────────────────────────────────
  // Also what a shared link previews as, so a detail route that left the
  // overview's title in place would make every component look alike in a chat
  // window. Declared here because it needs the resolved component above.
  const routeTitle =
    route.name === 'component' ? (componentHit?.component.name ?? null)
    : route.name === 'day' ? route.date
    : null;
  useEffect(() => {
    const audienceName = data?.audience?.name ?? null;
    document.title = [routeTitle, da.pageTitle, audienceName, da.brand].filter(Boolean).join(' — ');
  }, [data?.audience?.name, routeTitle]);

  // ── Incident filtering ──────────────────────────────────────────────
  const filterOptions = useMemo<IncidentFilterOption[]>(() => {
    const counts = new Map<StatusFilterLabel, number>();
    for (const incident of incidents) {
      for (const label of incident.labels ?? []) counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    // Canonical order, and only chips that would actually match something.
    return STATUS_FILTER_LABELS.filter((label) => (counts.get(label) ?? 0) > 0).map((label) => ({
      value: label,
      label: da.filterLabels[label],
      count: counts.get(label) ?? 0,
    }));
  }, [incidents]);

  const normalizedQuery = query.trim().toLowerCase();
  const isFiltered = selectedLabels.length > 0 || normalizedQuery.length > 0;

  const visibleIncidents = useMemo(() => {
    const selected = new Set(selectedLabels);
    return incidents
      .filter((incident) => {
        if (selected.size > 0 && !(incident.labels ?? []).some((label) => selected.has(label))) return false;
        if (!normalizedQuery) return true;
        return incidentSearchText(incident).includes(normalizedQuery);
      })
      .sort(byPinnedThenRecent);
  }, [incidents, selectedLabels, normalizedQuery]);

  const toggleLabel = useCallback((label: StatusFilterLabel) => {
    setSelectedLabels((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
  }, []);
  const clearFilters = useCallback(() => {
    setSelectedLabels([]);
    setQuery('');
  }, []);

  // ── Render ──────────────────────────────────────────────────────────
  let main: ReactNode;

  if (route.name === 'incident') {
    main = (
      <IncidentDetail
        incident={detailIncident}
        state={detailState}
        onBack={goHome}
        backHref={overviewHref}
      />
    );
  } else if (route.name === 'manage' || route.name === 'unsubscribe') {
    // Renders WITHOUT waiting on the summary payload: this page is about the
    // reader's own subscription, and holding a confirmation behind a status
    // fetch would make it fail exactly when the platform is struggling.
    main = (
      <SubscriptionPage
        mode={route.name}
        code={route.code}
        manageKey={route.key}
        linkError={route.name === 'manage' ? route.error : false}
        onBack={goHome}
        backHref={overviewHref}
      />
    );
  } else if (route.name === 'day') {
    // Placed with the other detail routes, ABOVE the loading guard, so it shows
    // its back link and a spinner rather than a bare loading card.
    main = (
      <DayDetail
        date={route.date}
        componentId={route.componentId}
        incidents={incidents}
        components={data?.components ?? NO_COMPONENTS}
        // The SUMMARY's state, not detailState — that one tracks the incident
        // LOOKUP, which never resolves on a route with no incident ref, so the
        // day page sat on its spinner forever with the data already in hand.
        state={data ? 'ready' : (status === 'error' ? 'notfound' : 'loading')}
        onBack={goHome}
        backHref={overviewHref}
        hrefForIncident={(ref) => buildStatusPath({ name: 'incident', audience, ref })}
        onOpenIncident={(ref) => navigate({ name: 'incident', audience, ref })}
      />
    );
  } else if (route.name === 'component') {
    main = (
      <ComponentDetail
        component={componentHit?.component ?? null}
        ancestors={componentHit?.ancestors ?? NO_ANCESTORS}
        incidents={componentIncidents}
        state={componentHit ? 'ready' : data ? 'notfound' : 'loading'}
        historyDays={historyDays}
        historyStartDate={historyStartDate}
        dayMinutes={dayMinutes ?? NO_MINUTES}
        hrefForDay={hrefForDay}
        onOpenDay={openDay}
        onBack={goHome}
        backHref={overviewHref}
        onOpenIncident={openIncident}
        hrefForIncident={hrefForIncident}
        onOpenComponent={openComponent}
        hrefForComponent={hrefForComponent}
      />
    );
  } else if (!data && status === 'loading') {
    main = (
      <div className="sp-card sp-card-pad sp-loading" role="status" aria-live="polite">
        <SIcon name="spinner" size={20} className="sp-spin" />
        <span>{da.loading}</span>
      </div>
    );
  } else if (!data) {
    main = (
      <div className="sp-card sp-card-pad sp-empty" role="alert">
        <SIcon name="degraded" size={24} />
        <p>{da.loadFailed}</p>
        <button type="button" className="sp-btn sp-btn--primary" onClick={handleRefresh}>
          {da.retry}
        </button>
      </div>
    );
  } else {
    main = (
      <>
        <StatusBanner
          overall={data.overall}
          overallLabel={data.overallLabel}
          generatedAt={data.generatedAt}
          activeIncidents={activeIncidents}
          onOpenIncident={openIncident}
          hrefForIncident={hrefForIncident}
        />

        <section className="sp-section" aria-labelledby="sp-overview-heading">
          <div className="sp-section-head">
            <h2 className="sp-section-title" id="sp-overview-heading">
              {da.overviewHeading}
            </h2>
            <p className="sp-section-sub">{da.overviewDays(historyDays)}</p>
          </div>
          <div className="sp-card sp-card-pad">
            {/* NO `info` on this bar, deliberately. It answers "is anything wrong,
                and how has that gone" — folding announcements in would make quiet
                days look eventful. Markers belong on the individual system bars
                (b-01598), and the server does not fold a mask for this bar either,
                so there is nothing to pass even by accident. */}
            <HistoryGrid
              history={overviewHistory}
              // Day navigation DOES apply here — clicking a cell on the
              // page-wide bar opens that day across every system. It is only the
              // information MARKERS that this bar leaves out.
              hrefForDay={hrefForDay}
              onOpenDay={openDay}
              historyDays={historyDays}
              historyStartDate={historyStartDate}
              downtime={overviewDowntime}
              dayMinutes={dayMinutes}
              label={data.audience.name ?? da.audienceAll}
              showLegend
              size="lg"
            />
          </div>
        </section>

        <section className="sp-section" aria-labelledby="sp-components-heading">
          <div className="sp-section-head">
            <h2 className="sp-section-title" id="sp-components-heading">
              {da.componentsHeading}
            </h2>
          </div>
          {allComponents.length === 0 ? (
            <div className="sp-card sp-card-pad sp-empty">
              <p>{da.componentsEmpty}</p>
            </div>
          ) : (
            <ComponentTree
              components={data.components}
              historyDays={historyDays}
              historyStartDate={historyStartDate}
              dayMinutes={dayMinutes}
              onOpenComponent={openComponent}
              hrefForComponent={hrefForComponent}
              hrefForDay={hrefForDay}
              onOpenDay={openDay}
            />
          )}
        </section>

        <section className="sp-section" aria-labelledby="sp-incidents-heading">
          <div className="sp-section-head">
            <h2 className="sp-section-title" id="sp-incidents-heading">
              {da.incidentsHeading}
            </h2>
          </div>
          {filterOptions.length > 0 && (
            <IncidentFilters
              options={filterOptions}
              selected={selectedLabels}
              onToggle={toggleLabel}
              onClear={clearFilters}
              query={query}
              onQueryChange={setQuery}
            />
          )}
          <IncidentList
            incidents={visibleIncidents}
            filtered={isFiltered}
            onOpenIncident={openIncident}
            hrefForIncident={hrefForIncident}
          />
        </section>
      </>
    );
  }

  return (
    <>
      <a className="sp-skip" href={`#${STATUS_MAIN_ID}`}>
        {da.skipToContent}
      </a>
      <StatusShell
        header={
          <StatusHeader
            audienceName={data?.audience?.name ?? null}
            subscribeEnabled={subscribeEnabled}
            onSubscribe={() => setSubscribeOpen(true)}
            onHome={goHome}
            homeHref={overviewHref}
          />
        }
        footer={
          <StatusFooter
            generatedAt={data?.generatedAt ?? null}
            fetchedAt={fetchedAt}
            source={source}
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
        }
      >
        {data && source === 'snapshot' && (
          <DegradedNotice
            generatedAt={data.generatedAt}
            fetchedAt={fetchedAt}
            failures={failures}
            onRetry={handleRefresh}
          />
        )}
        {main}
      </StatusShell>
      {subscribeEnabled && subscribeOpen && (
        <SubscribeSheet
          open={subscribeOpen}
          onClose={() => setSubscribeOpen(false)}
          initialAudienceSlug={data?.audience?.slug ?? audience}
        />
      )}
    </>
  );
}
